/**
 * AdminSubscriptionsEditor — SMS admin (Users › edit) adapter for the shared
 * `@tiaki/profile-ui` `UserSubscriptionsEditor`.
 *
 * The admin counterpart of the profile-surface `ProfileSubscriptionsEditor`:
 * same shared presentational editor, same data wiring, but for on-behalf
 * editing of an arbitrary `userId` rather than the current user.
 *
 * The shared editor is presentational + controlled — it renders the
 * protocol/alert/drill table and (for multi-site users) a site switcher, but
 * owns no data. This wrapper supplies everything app-specific:
 *   - the per-user subscription data hooks (`useUserSubscriptions` +
 *     the set/clear override mutations);
 *   - the `accounts.can_manage_subscription` permission;
 *   - the site-switcher selection state (the shared editor is controlled —
 *     `selectedSchoolId` + `onSchoolChange`), feeding the effective site to
 *     `useUserSubscriptions` so the backend `?school=` filter narrows the list;
 *   - the error toast (the lib has no toaster).
 *
 * The admin Users › edit form passes only `userId` (no `schools`), so the
 * editor renders the flat all-sites list and no switcher appears. Pass
 * `schools` to opt into per-site scoping.
 */
import { useMemo, useState } from 'react';
import { useHasPermission } from '@aegix/auth-permissions';
import {
  UserSubscriptionsEditor,
  type SiteOption,
  type UserSubscriptionOverridePayload,
} from '@tiaki/profile-ui';
import {
  useClearUserSubscriptionOverride,
  useSetUserSubscriptionOverride,
  useUserSubscriptions,
} from '../../hooks/queries/useUserSubscriptions';
import { useFeature } from '../../hooks/useFeature';
import { FeatureCode } from '../../types/features';
import { toaster } from '../ui/toaster';

interface AdminSubscriptionsEditorProps {
  /** The user whose subscriptions are being edited (the edited user). */
  userId: number;
  /** The edited user's sites — the site-switcher candidates.
   *  Omit/empty → the flat all-sites list (admin Users › edit default). */
  schools?: SiteOption[];
}

export function AdminSubscriptionsEditor({
  userId,
  schools,
}: AdminSubscriptionsEditorProps) {
  const canManageSubscriptions = useHasPermission(
    'accounts.can_manage_subscription',
  );
  // ADR-117 Mechanism B gate — drops the "1st/2nd Escalation"
  // options on both axes and clamps an already-stored level 1/2 to the
  // "Alert Start" label. Fails closed via `useFeature`.
  const escalationEnabled = useFeature(FeatureCode.ALERT_ESCALATION);

  // Site-downline scoping. The shared editor is controlled, so we
  // hold the selection here and resolve the effective site (first by default;
  // re-clamped if the selection falls outside the current site list) to feed
  // the data hook's `?school=` filter.
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
  const effectiveSchoolId = useMemo(() => {
    if (!schools || schools.length === 0) return null;
    if (
      selectedSchoolId != null &&
      schools.some((s) => s.id === selectedSchoolId)
    ) {
      return selectedSchoolId;
    }
    return schools[0]?.id ?? null;
  }, [schools, selectedSchoolId]);

  const { data, isLoading } = useUserSubscriptions(userId, effectiveSchoolId);
  const setMut = useSetUserSubscriptionOverride(userId);
  const clearMut = useClearUserSubscriptionOverride(userId);

  const onError = (err: unknown) => {
    toaster.create({
      title: 'Failed to update subscription',
      description: err instanceof Error ? err.message : undefined,
      type: 'error',
      duration: 4000,
    });
  };

  return (
    <UserSubscriptionsEditor
      rows={data?.results ?? []}
      loading={isLoading}
      isMutating={setMut.isPending || clearMut.isPending}
      canManage={canManageSubscriptions}
      escalationEnabled={escalationEnabled}
      schools={schools}
      selectedSchoolId={effectiveSchoolId}
      onSchoolChange={setSelectedSchoolId}
      onSetOverride={(
        alertTypeId: number,
        body: UserSubscriptionOverridePayload,
      ) => setMut.mutate({ alertTypeId, body }, { onError })}
      onClearOverride={(alertTypeId: number) =>
        clearMut.mutate({ alertTypeId }, { onError })
      }
    />
  );
}

export default AdminSubscriptionsEditor;
